/**
 * Parsing and validation for the OAuth parameters Zapier sends to the authorize page.
 *
 * Zapier opens the authorize URL with the parameters its `oauth2Config.authorizeUrl` declares,
 * plus the PKCE pair it computes itself. The page is a transparent proxy: everything Zapier
 * sends is forwarded to Kinde untouched, so this module only identifies what must be present
 * and never rewrites a value.
 */

/**
 * Parameters Kinde cannot complete the handshake without. `code_challenge` is deliberately
 * absent: PKCE is Zapier's to enable, and treating it as required here would break the flow the
 * moment the app version changes.
 */
export const REQUIRED_AUTHORIZE_PARAMS = ['client_id', 'redirect_uri', 'state', 'response_type']

/**
 * parseAuthorizeParams - Reads Zapier's OAuth parameters off the URL.
 *
 * @param {URLSearchParams} searchParams - The authorize page's query string.
 * @returns {{ params: Object<string, string>, missing: string[] }} Every parameter as a plain
 *   object, and the names of any required parameter that is absent or blank.
 */
export const parseAuthorizeParams = (searchParams) => {
    const params = {}

    for (const [key, value] of searchParams.entries()) {
        params[key] = value
    }

    const missing = REQUIRED_AUTHORIZE_PARAMS.filter((name) => !params[name]?.trim())

    return { params, missing }
}

/**
 * isExpectedZapierClient - Whether the request carries our Zapier application's client id.
 *
 * Without `VITE_ZAPIER_CLIENT_ID` configured the check passes, so local and preview
 * environments keep working before the value is wired up. Kinde still validates `redirect_uri`
 * against the callbacks registered for whatever client id is sent, so this is defence in depth
 * rather than the thing that stops a forged handshake.
 *
 * @param {string} [clientId] - The `client_id` from the query string.
 * @returns {boolean} True when no expected client id is configured, or the two match.
 */
export const isExpectedZapierClient = (clientId) => {
    const expected = import.meta.env.VITE_ZAPIER_CLIENT_ID

    return !expected || clientId === expected
}
